// "Alerts when the app is closed": Web Push on this device, in the bell's panel (canvas
// board AlertsPhone, docs/plans/push-alerts.md). The device's subscription is saved with
// push_subscribe; what is sent, and to whom, is the server's (_push_due).
import { Button, Switch, useToast } from '@ims/ui'
import { Info } from '@ims/ui/icons'
import { useEffect, useState } from 'react'
import { errorMessage, useOnline } from '../hooks.ts'
import { createApi } from '../lib/api.ts'
import { useSession } from '../session.tsx'
import { useWorkspace } from '../workspace.tsx'

type State = 'loading' | 'off' | 'on'

/** The bell speaks of tables; Settings → Alerts of this phone and its alerts. */
export function PushSwitch({
  label = 'Alerts when the app is closed',
  description = 'This phone buzzes for your tables even when it is locked.',
}: { label?: string; description?: string } = {}) {
  const { platform } = useSession()
  const { current } = useWorkspace()
  const online = useOnline()
  const toast = useToast()
  const push = platform.push
  const tenantId = current?.tenant_id ?? ''
  const [state, setState] = useState<State>('loading')
  const [busy, setBusy] = useState(false)
  const [denied, setDenied] = useState(() => push?.permission() === 'denied')

  // On only when this browser has a subscription the server knows for this business.
  useEffect(() => {
    if (!push?.supported() || !tenantId) return
    let active = true
    void (async () => {
      const device = await push.current()
      const known = device
        ? (
            await platform.supabase.rpc('push_status', {
              p_tenant_id: tenantId,
              p_endpoint: device.endpoint,
            })
          ).data === true
        : false
      if (active) setState(known ? 'on' : 'off')
    })().catch(() => active && setState('off'))
    return () => {
      active = false
    }
  }, [push, tenantId, platform])

  if (!push?.publicKey) return null
  const supported = push.supported()
  const why = push.needsInstall()
    ? 'On iPhone, add Sano Godam to the Home Screen first, then turn this on there.'
    : !supported
      ? 'This browser can’t get alerts while it is closed.'
      : denied
        ? 'Notifications are blocked for this site. Allow them in the phone’s settings.'
        : !online
          ? 'Needs the internet to turn on or off.'
          : null

  const turn = async (on: boolean) => {
    setBusy(true)
    try {
      if (on) {
        const device = await push.subscribe()
        if (!device) {
          setDenied(push.permission() === 'denied')
          return
        }
        const { error } = await platform.supabase.rpc('push_subscribe', {
          p_tenant_id: tenantId,
          p_endpoint: device.endpoint,
          p_p256dh: device.p256dh,
          p_auth: device.auth,
          p_user_agent: navigator.userAgent.slice(0, 300),
        })
        if (error) throw error
        setState('on')
      } else {
        const endpoint = await push.unsubscribe()
        if (endpoint) await platform.supabase.rpc('push_unsubscribe', { p_endpoint: endpoint })
        setState('off')
      }
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  const test = async () => {
    setBusy(true)
    try {
      const { sent } = await createApi(platform.apiUrl, platform.supabase).pushTest(tenantId)
      if (sent > 0) toast.success('Test alert sent', { detail: 'Lock the phone: it should buzz.' })
      else toast.warning('No phone got it', { detail: 'Turn the switch off and on again.' })
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <Switch
        label={label}
        description={why ?? description}
        checked={state === 'on'}
        disabled={why !== null || busy || state === 'loading'}
        onChange={(e) => void turn(e.target.checked)}
      />
      {state === 'on' && (
        <div className="mt-3 flex gap-2 rounded-lg bg-soft px-3 py-2.5 text-xs leading-relaxed text-muted">
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
          <div className="space-y-2">
            <p>
              On for <b className="text-ink">this phone</b>. Some phones hold alerts back to save
              battery.
            </p>
            <details>
              <summary className="cursor-pointer font-semibold text-primary">
                Let the app run in the background
              </summary>
              <ul className="mt-1.5 list-disc space-y-1 pl-4">
                <li>
                  Android: Settings → Apps → Chrome → Battery → Unrestricted (on Xiaomi, Oppo, Vivo
                  and Realme also turn on Autostart).
                </li>
                <li>Keep notifications for Chrome on, with sound.</li>
                <li>iPhone: alerts come only to Sano Godam added to the Home Screen.</li>
              </ul>
            </details>
            <Button size="sm" variant="secondary" loading={busy} onClick={() => void test()}>
              Send me a test alert
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
